'use client';

import { useEffect, useRef } from 'react';
import type { ProductListItem } from '@cobrastyle/shared-types';
import { useAnalytics } from '@/hooks';

interface ProductListTrackerProps {
  products: ProductListItem[];
  listId: string;
  listName: string;
}

/**
 * Client component that tracks view_item_list events when products are displayed.
 * Wrap this around product grids to enable GA4 product list tracking.
 */
export function ProductListTracker({
  products,
  listId,
  listName,
}: ProductListTrackerProps) {
  const { trackViewItemList } = useAnalytics();
  const trackedListId = useRef<string | null>(null);

  // Track list view - intentionally limited deps to avoid re-tracking on every render
  useEffect(() => {
    // Only track if we have products and haven't tracked this exact list yet
    if (products.length > 0 && trackedListId.current !== listId) {
      trackViewItemList(products, listId, listName);
      trackedListId.current = listId;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listId, products.length]); // Only depend on listId and count, not the full array or callback

  // This component is just for tracking, it renders nothing
  return null;
}
